/**
 * ═══════════════════════════════════════════════════════════════
 * UNIFIED ACCESS CONTROL HOOK
 * ═══════════════════════════════════════════════════════════════
 * 
 * Check if user has access via:
 * - Active subscription (Stripe)
 * - Active free trial (14-day unified trial - includes ATS + TSE)
 * 
 * Usage:
 *   const { hasAccess, accessType, loading, subscription, trial, productTypes } = useAccessControl();
 * 
 * Returns:
 *   hasAccess: true if user has access (sub OR trial)
 *   accessType: 'subscription' | 'trial' | 'trial_expired' | 'none'
 *   loading: true while checking
 *   subscription: subscription data if active
 *   trial: trial data if active or expired
 *   error: error message if any
 *   productTypes: array of product types user has access to ['ats', 'tse', 'bundle']
 *     - Bundle subscription: ['ats', 'tse', 'bundle']
 *     - ATS only: ['ats']
 *     - TSE only: ['tse']
 *     - Trial: ['ats', 'tse']
 *   planId: internal plan ID (e.g., 'ats_small', 'bundle_large') or null
 */

import { useState, useEffect } from 'react';
import { ref, get, onValue } from 'firebase/database';
import { database } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { getPlanIdFromProductId, ATS_PLANS, TSE_PLANS, BUNDLE_PLANS } from '../utils/featureFlags';

const initialAccessState = {
  hasAccess: false,
  accessType: 'none',
  loading: true,
  subscription: null,
  trial: null,
  error: null,
  productTypes: [],
  planId: null
};

const unauthenticatedAccessState = {
  ...initialAccessState,
  loading: false,
  error: 'Not authenticated'
};

let accessState = initialAccessState;
let activeUserId = null;
let startupUserId = null;
let subscriptionUnsubscribe = null;
let trialUnsubscribe = null;
const accessListeners = new Set();

const emitAccessState = (nextState) => {
  accessState = nextState;
  accessListeners.forEach((listener) => listener(accessState));
};

const cleanupAccessListeners = () => {
  if (subscriptionUnsubscribe) {
    subscriptionUnsubscribe();
    subscriptionUnsubscribe = null;
  }

  if (trialUnsubscribe) {
    trialUnsubscribe();
    trialUnsubscribe = null;
  }
};

const subscribeToTrialAccess = (companyUID) => {
  if (trialUnsubscribe) return;

  const trialRef = ref(database, `companies/${companyUID}/trial`);

  trialUnsubscribe = onValue(trialRef, (snapshot) => {
    const trialData = snapshot.val();
    const now = Date.now();

    if (trialData && trialData.status === 'active' && trialData.expiresAt > now) {
      emitAccessState({
        hasAccess: true,
        accessType: 'trial',
        loading: false,
        subscription: null,
        trial: {
          ...trialData,
          daysRemaining: Math.ceil((trialData.expiresAt - now) / (24 * 60 * 60 * 1000)),
          hoursRemaining: Math.ceil((trialData.expiresAt - now) / (60 * 60 * 1000))
        },
        error: null,
        productTypes: ['ats', 'tse'],
        planId: null
      });
      return;
    }

    if (trialData && (trialData.status === 'expired' || trialData.status === 'active')) {
      emitAccessState({
        hasAccess: false,
        accessType: 'trial_expired',
        loading: false,
        subscription: null,
        trial: {
          ...trialData,
          daysRemaining: 0,
          expiredDaysAgo: Math.floor((now - trialData.expiresAt) / (24 * 60 * 60 * 1000))
        },
        error: null,
        productTypes: [],
        planId: null
      });
      return;
    }

    emitAccessState({
      hasAccess: false,
      accessType: 'none',
      loading: false,
      subscription: null,
      trial: null,
      error: null,
      productTypes: [],
      planId: null
    });
  }, (error) => {
    console.error('Error checking trial:', error);
    emitAccessState({
      ...accessState,
      loading: false,
      error: error.message
    });
  });
};

const startAccessControl = async (currentUser) => {
  if (!currentUser) {
    activeUserId = null;
    startupUserId = null;
    cleanupAccessListeners();
    emitAccessState(unauthenticatedAccessState);
    return;
  }

  if (activeUserId === currentUser.uid && (startupUserId === currentUser.uid || subscriptionUnsubscribe)) {
    return;
  }

  const previousUserId = activeUserId;
  activeUserId = currentUser.uid;
  startupUserId = currentUser.uid;
  cleanupAccessListeners();
  emitAccessState({
    ...initialAccessState,
    loading: previousUserId !== currentUser.uid || accessState.loading
  });

  try {
    let companyUID = currentUser.uid;
    const collabSnapshot = await get(ref(database, `collaborators/${currentUser.uid}`));

    if (collabSnapshot.exists()) {
      const collaboratorData = collabSnapshot.val() || {};
      companyUID = collaboratorData.companyId || collaboratorData.company_uid || currentUser.uid;
    }

    if (activeUserId !== currentUser.uid) return;

    const subRef = ref(database, `companies/${companyUID}/subscription`);

    subscriptionUnsubscribe = onValue(subRef, (snapshot) => {
      startupUserId = null;
      const subData = snapshot.val();

      if (subData && (subData.status === 'active' || subData.status === 'trialing' || subData.status === 'unlimited')) {
        let productTypes = [];

        if (subData.productType === 'bundle') {
          productTypes = ['ats', 'tse', 'bundle'];
        } else if (subData.productType === 'tse') {
          productTypes = ['tse'];
        } else if (subData.productType === 'ats') {
          productTypes = ['ats'];
        } else {
          productTypes = ['ats'];
        }

        let planId = null;
        const productId = subData.productId || subData.product_id;
        if (productId) {
          planId = getPlanIdFromProductId(productId);
        } else if (subData.planId) {
          planId = subData.planId;
        }

        const isValidPlanId = Boolean(
          planId &&
          (ATS_PLANS[planId] !== undefined ||
            TSE_PLANS[planId] !== undefined ||
            BUNDLE_PLANS[planId] !== undefined)
        );

        if (!isValidPlanId) {
          console.warn(`[SECURITY] Invalid planId detected: "${planId}". Access denied even though subscription status is active.`);
          subscribeToTrialAccess(companyUID);
          return;
        }

        if (trialUnsubscribe) {
          trialUnsubscribe();
          trialUnsubscribe = null;
        }

        emitAccessState({
          hasAccess: true,
          accessType: 'subscription',
          loading: false,
          subscription: subData,
          trial: null,
          error: null,
          productTypes,
          planId
        });
        return;
      }

      subscribeToTrialAccess(companyUID);
    }, (error) => {
      startupUserId = null;
      console.error('Error checking access:', error);
      emitAccessState({
        ...accessState,
        loading: false,
        error: error.message
      });
    });
  } catch (error) {
    startupUserId = null;
    console.error('Error checking access:', error);
    emitAccessState({
      ...accessState,
      loading: false,
      error: error.message
    });
  }
};

export const useAccessControl = () => {
  const { currentUser } = useAuth();
  const [state, setState] = useState(accessState);

  useEffect(() => {
    accessListeners.add(setState);
    setState(accessState);
    startAccessControl(currentUser);

    return () => {
      accessListeners.delete(setState);
    };
  }, [currentUser]);

  return state;
};

export default useAccessControl;
